import { supabase } from './supabase.js'
import { todayISO } from './format.js'

/**
 * Subscriptions.
 *
 * A subscription is a named thing, a recurring amount, and a renewal day that
 * never ends. That is what `payable_defaults` already is — the rows that load
 * into every new payout — so subscriptions live there with a few extra fields
 * rather than in a second table that would also have to post into payouts.
 * Two things posting money is how a double charge happens.
 *
 * The one thing a default could not do before is a cadence longer than a month.
 * `every_n_months` plus `anchor_month` handles that: a yearly renewal lands in
 * its own month and nowhere else.
 */

export const CADENCES = [
  { months: 1,  label: 'Monthly' },
  { months: 3,  label: 'Every 3 months' },
  { months: 6,  label: 'Every 6 months' },
  { months: 12, label: 'Yearly' },
]

export const cadenceLabel = (n) =>
  CADENCES.find((c) => c.months === Number(n))?.label || 'Every ' + n + ' months'

/** True if this subscription bills in the given month (1–12). */
export function billsIn(sub, month) {
  const every = Math.max(1, Number(sub.every_n_months) || 1)
  if (every === 1) return true
  if (sub.anchor_month == null) return false
  return (((month - Number(sub.anchor_month)) % every) + every) % every === 0
}

/**
 * Whether a default payable loads into a given payout.
 *
 * A plain default loads into every payout, the way it always has. A
 * subscription differs on two counts: it bills once a month rather than once
 * per payout, and it may bill less often than monthly. `n` is which payout of
 * the month this is, so a second payout does not charge a subscription twice —
 * the same guard installment charges already have.
 */
export function loadsIntoPayout(d, monthKey, n) {
  if (!d.is_subscription) return true
  if (n !== 1) return false
  const every = Math.max(1, Number(d.every_n_months) || 1)
  if (every === 1) return true
  /* No anchor month means no month has claimed it yet. Skipping shows up on
     the Subscriptions screen, where a silent wrong charge would not. */
  if (d.anchor_month == null) return false
  const month = Number(String(monthKey).split('-')[1])
  return (((month - Number(d.anchor_month)) % every) + every) % every === 0
}

/** What it costs a month, with anything non-monthly spread across its term. */
export const perMonth = (sub) =>
  Number(sub.amount || 0) / Math.max(1, Number(sub.every_n_months) || 1)

export const perYear = (sub) => perMonth(sub) * 12

/** Clamps day 31 to the end of a short month, the way a real biller does. */
function onDay(year, monthIndex, day) {
  const last = new Date(year, monthIndex + 1, 0).getDate()
  return new Date(year, monthIndex, Math.min(day, last))
}

/**
 * The next date it renews, or null if he has not said which day. Walks forward
 * a month at a time rather than doing modular arithmetic on dates, because the
 * clamping above makes the shortcut wrong in February.
 */
export function nextRenewal(sub, from = new Date()) {
  if (!sub.renews_on) return null
  const day = Number(sub.renews_on)
  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  for (let i = 0; i < 24; i += 1) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1)
    if (!billsIn(sub, d.getMonth() + 1)) continue
    const hit = onDay(d.getFullYear(), d.getMonth(), day)
    if (hit >= today) return hit
  }
  return null
}

/** Whole months from a start date to today, inclusive of the first. */
export function monthsPaying(sub, from = new Date()) {
  if (!sub.started_on) return null
  const [y, m] = String(sub.started_on).slice(0, 10).split('-').map(Number)
  if (!y || !m) return null
  const months = (from.getFullYear() - y) * 12 + (from.getMonth() + 1 - m)
  return Math.max(0, months) + 1
}

/**
 * What it has cost so far. Counts actual billing events since the start, not
 * calendar months — a yearly plan two years in has charged twice, not 24 times.
 * This is the number that decides whether something gets cancelled, so it
 * counts rather than estimates.
 */
export function paidSoFar(sub, from = new Date()) {
  const months = monthsPaying(sub, from)
  if (months == null) return null
  const every = Math.max(1, Number(sub.every_n_months) || 1)
  const charges = Math.floor((months - 1) / every) + 1
  return charges * Number(sub.amount || 0)
}

/* ── Reads and writes ───────────────────────────────────────── */

/** Every default, subscription or not — the screen needs both lists. */
export async function getDefaults() {
  const { data, error } = await supabase
    .from('payable_defaults')
    .select('*')
    .order('sort_order')
  if (error) throw error
  return data ?? []
}

async function logIt(kind, summary, targetId) {
  try {
    await supabase.from('activity_log').insert({
      kind,
      summary,
      target_type: 'subscription',
      target_id: targetId,
    })
  } catch {
    /* the log is never load-bearing */
  }
}

/**
 * He picks one renewal date. The day and the anchor month both come out of it,
 * so there are not three fields to keep consistent by hand.
 */
export function fromRenewalDate(iso, everyNMonths) {
  if (!iso) return { renews_on: null, anchor_month: null }
  const [, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  return {
    renews_on: d || null,
    anchor_month: Number(everyNMonths) > 1 ? m || null : null,
  }
}

export async function saveSubscription(sub) {
  const patch = {
    name: sub.name.trim(),
    amount: sub.amount,
    every_n_months: sub.everyNMonths,
    started_on: sub.startedOn || null,
    cancel_by: sub.cancelBy || null,
    note: sub.note?.trim() || null,
    is_subscription: true,
    ...fromRenewalDate(sub.renewsOn, sub.everyNMonths),
  }

  if (sub.id) {
    const { data, error } = await supabase
      .from('payable_defaults')
      .update(patch)
      .eq('id', sub.id)
      .select()
      .single()
    if (error) throw error
    await logIt('subscription.edit', patch.name + ' updated', data.id)
    return data
  }

  const { data, error } = await supabase
    .from('payable_defaults')
    .insert({ ...patch, category_id: sub.categoryId ?? null, sort_order: sub.sortOrder ?? 0 })
    .select()
    .single()
  if (error) throw error
  await logIt('subscription.add', patch.name + ' added', data.id)
  return data
}

/** Marks an existing monthly item as a subscription. Nothing else changes. */
export async function markAsSubscription(id, on = true) {
  const { data, error } = await supabase
    .from('payable_defaults')
    .update({ is_subscription: on })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function setUnused(id, unused) {
  const { error } = await supabase
    .from('payable_defaults')
    .update({ unused })
    .eq('id', id)
  if (error) throw error
}

/**
 * Removing a subscription stops it loading into future payouts. Payables it
 * already created stay where they are — they are what he actually paid, and
 * rewriting history to tidy a list is how a ledger stops being trustworthy.
 */
export async function removeSubscription(id, name) {
  const { error } = await supabase.from('payable_defaults').delete().eq('id', id)
  if (error) throw error
  await logIt('subscription.remove', name + ' removed', null)
}

/** Roll-up for the header. Kept arithmetic — no verdict on the number. */
export function totals(subs, month = new Date().getMonth() + 1) {
  const live = subs.filter((s) => !s.unused)
  const idle = subs.filter((s) => s.unused)
  return {
    perMonth: subs.reduce((s, x) => s + perMonth(x), 0),
    perYear: subs.reduce((s, x) => s + perYear(x), 0),
    thisMonth: subs.filter((s) => billsIn(s, month)).reduce((s, x) => s + Number(x.amount || 0), 0),
    idlePerMonth: idle.reduce((s, x) => s + perMonth(x), 0),
    idlePerYear: idle.reduce((s, x) => s + perYear(x), 0),
    liveCount: live.length,
    idleCount: idle.length,
  }
}

/** Soonest renewal first, undated last, things he flagged unused at the bottom. */
export function sortSubs(subs, from = new Date()) {
  return [...subs].sort((a, b) => {
    if (!!a.unused !== !!b.unused) return a.unused ? 1 : -1
    const ra = nextRenewal(a, from)
    const rb = nextRenewal(b, from)
    if (!ra && !rb) return a.name.localeCompare(b.name)
    if (!ra) return 1
    if (!rb) return -1
    return todayISO(ra).localeCompare(todayISO(rb)) || a.name.localeCompare(b.name)
  })
}
